import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2, Search, Plus, PackageCheck, Wrench, RefreshCw, Factory,
  IndianRupee, Gauge, BarChart3, ShieldQuestion, Sparkles, X, ChevronRight,
} from "lucide-react";

const TABS = [
  { k: "requests", label: "Requests", bucket: "open" },
  { k: "eligibility", label: "Eligibility", bucket: "eligibility" },
  { k: "inbound", label: "Inbound", bucket: "inbound" },
  { k: "inspection", label: "Inspection", bucket: "inspection" },
  { k: "repair", label: "Repair", bucket: "repair" },
  { k: "replacement", label: "Replacement", bucket: "replacement" },
  { k: "oem", label: "OEM / Vendor RMA", bucket: "oem" },
  { k: "recovery", label: "Recovery", bucket: "recovery" },
  { k: "sla", label: "SLA" },
  { k: "analytics", label: "Analytics" },
];

const STATUS_TINT = {
  REQUESTED: "bg-slate-100 text-slate-600 border-slate-200",
  ELIGIBILITY_REVIEW: "bg-amber-50 text-amber-700 border-amber-200",
  APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  COLLECTION_PENDING: "bg-sky-50 text-sky-700 border-sky-200",
  RECEIVED: "bg-indigo-50 text-indigo-700 border-indigo-200",
  INSPECTION: "bg-violet-50 text-violet-700 border-violet-200",
  OEM_RMA: "bg-orange-50 text-orange-700 border-orange-200",
  REPAIR: "bg-blue-50 text-blue-700 border-blue-200",
  REPLACEMENT: "bg-cyan-50 text-cyan-700 border-cyan-200",
  DISPATCH: "bg-teal-50 text-teal-700 border-teal-200",
  DELIVERED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  RECOVERY_PENDING: "bg-rose-50 text-rose-700 border-rose-200",
  CLOSED: "bg-slate-100 text-slate-500 border-slate-200",
  REJECTED: "bg-rose-100 text-rose-700 border-rose-200",
};
const ELIG_TINT = {
  ELIGIBLE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  NOT_ELIGIBLE: "bg-rose-50 text-rose-700 border-rose-200",
  REVIEW_REQUIRED: "bg-amber-50 text-amber-700 border-amber-200",
};
const inr = (n) => (n == null ? "—" : "₹" + Number(n).toLocaleString("en-IN"));

export default function RmaControlCentre() {
  const [tab, setTab] = useState("requests");
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);   // case detail
  const [createOpen, setCreateOpen] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [sla, setSla] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiQ, setAiQ] = useState("");
  const [aiAns, setAiAns] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => { api.get("/rma/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const load = useCallback(async (t = tab, query = q) => {
    setLoading(true);
    try {
      if (t === "analytics") { const { data } = await api.get("/rma/analytics"); setAnalytics(data); }
      else if (t === "sla") { const { data } = await api.get("/rma/sla"); setSla(data); }
      else {
        const bucket = TABS.find((x) => x.k === t)?.bucket;
        const { data } = await api.get("/rma/cases", { params: { bucket, q: query } });
        setData(data);
      }
    } catch { toast.error("Couldn't load RMA data"); }
    finally { setLoading(false); }
  }, [tab, q]);

  useEffect(() => { load(tab, q); }, [tab]); // eslint-disable-line

  const openCase = async (id) => {
    try { const { data } = await api.get(`/rma/cases/${id}`); setActive(data); }
    catch { toast.error("Couldn't open case"); }
  };

  const askAi = async () => {
    if (!aiQ.trim()) return;
    setAiBusy(true); setAiAns("");
    try { const { data } = await api.post("/rma/ai", { query: aiQ.trim() }); setAiAns(data.answer); }
    catch { setAiAns("AI is unavailable right now."); }
    finally { setAiBusy(false); }
  };

  const counts = data.counts || {};

  return (
    <div className="max-w-7xl mx-auto p-5 space-y-5" data-testid="rma-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Service</div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight mt-1">RMA &amp; Warranty Recovery</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">Returns, repairs, replacements and OEM / distributor
            warranty recovery — over your existing Service Desk, Asset, Stock &amp; Shipment estate.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { setAiOpen(true); setAiAns(""); setAiQ(""); }} data-testid="rma-ai-btn">
            <Sparkles className="h-4 w-4 mr-1 text-violet-600" /> Ask ONE AI
          </Button>
          {meta?.can_write && (
            <Button onClick={() => setCreateOpen(true)} data-testid="rma-new-btn" className="bg-slate-900 hover:bg-slate-800 text-white">
              <Plus className="h-4 w-4 mr-1" /> New RMA
            </Button>
          )}
        </div>
      </div>

      <nav className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`rma-tab-${t.k}`}
            className={"px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px " +
              (tab === t.k ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800")}>
            {t.label}
            {t.bucket && counts[t.bucket] ? <span className="text-[10px] rounded-full bg-slate-100 text-slate-600 px-1.5">{counts[t.bucket]}</span> : null}
          </button>
        ))}
      </nav>

      {loading ? (
        <div className="py-20 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : tab === "analytics" ? (
        <Analytics a={analytics} />
      ) : tab === "sla" ? (
        <SlaView s={sla} />
      ) : (
        <>
          <div className="relative w-72 mb-2">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(tab, q)}
              placeholder="Search RMA no / serial / customer…" className="pl-8 h-9" data-testid="rma-search" />
          </div>
          {(data.items || []).length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm" data-testid="rma-empty">No RMA cases in this queue.</div>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              {data.items.map((c) => (
                <button key={c.id} onClick={() => openCase(c.id)} data-testid={`rma-row-${c.id}`}
                  className="w-full grid grid-cols-12 gap-2 px-4 py-3 items-center border-b border-slate-50 hover:bg-slate-50 text-left">
                  <div className="col-span-3 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{c.rma_no}</div>
                    <div className="text-[11px] text-slate-400 truncate">{c.serial || "no serial"}</div>
                  </div>
                  <div className="col-span-3 text-[13px] text-slate-600 truncate">{c.product || "—"}</div>
                  <div className="col-span-2 text-[13px] text-slate-500 truncate">{c.customer_name || "—"}</div>
                  <div className="col-span-2">
                    <Badge className={"text-[10px] border " + (STATUS_TINT[c.status] || STATUS_TINT.REQUESTED)}>{c.status?.replace(/_/g, " ")}</Badge>
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-2">
                    {c.eligibility?.result && (
                      <Badge className={"text-[10px] border " + (ELIG_TINT[c.eligibility.result] || "")}>{c.eligibility.result.replace(/_/g, " ")}</Badge>
                    )}
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {active && (
        <CaseDrawer c={active} meta={meta} onClose={() => setActive(null)}
          reload={() => { openCase(active.id); load(tab, q); }} />
      )}
      {createOpen && <CreateDialog meta={meta} onClose={() => setCreateOpen(false)}
        onCreated={(c) => { setCreateOpen(false); load(tab, q); openCase(c.id); }} />}

      <Dialog open={aiOpen} onOpenChange={(o) => !o && setAiOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="rma-ai-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" /> Ask ONE AI · RMA</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-1.5">
              {["Show RMAs awaiting OEM response", "Which warranty claims have not been recovered?", "Which vendor owes us credits?", "Show repeat failures"].map((s) => (
                <button key={s} onClick={() => setAiQ(s)} className="text-[11px] px-2 py-1 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50">{s}</button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={aiQ} onChange={(e) => setAiQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && askAi()}
                placeholder="Ask about warranty, OEM claims, recovery…" data-testid="rma-ai-input" />
              <Button onClick={askAi} disabled={aiBusy} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="rma-ai-send">
                {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ask"}
              </Button>
            </div>
            {aiAns && <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700" data-testid="rma-ai-answer">{aiAns}</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
      </div>
      <div className={"mt-1 text-2xl font-semibold tabular-nums " + (tone || "text-slate-900")}>{value}</div>
    </div>
  );
}

function Analytics({ a }) {
  if (!a) return <div className="text-sm text-slate-400">No analytics.</div>;
  return (
    <div className="space-y-5" data-testid="rma-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={BarChart3} label="Total RMAs" value={a.total} />
        <Stat icon={Wrench} label="Repair rate" value={a.repair_rate + "%"} />
        <Stat icon={RefreshCw} label="Replacement rate" value={a.replacement_rate + "%"} />
        <Stat icon={IndianRupee} label="Recovery rate" value={a.recovery.recovery_rate + "%"} tone="text-emerald-700" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel title="Top failure products">
          {a.top_failure_products.map((r) => <Bar key={r.key} label={r.key} v={r.count} max={a.top_failure_products[0]?.count} />)}
          {!a.top_failure_products.length && <Empty />}
        </Panel>
        <Panel title="By fault code">
          {a.by_fault_code.map((r) => <Bar key={r.key} label={r.key} v={r.count} max={a.by_fault_code[0]?.count} />)}
          {!a.by_fault_code.length && <Empty />}
        </Panel>
        <Panel title="Repeat-failure serials">
          {a.repeat_failure_serials.map((r) => (
            <div key={r.serial} className="flex justify-between text-sm py-1"><span className="text-slate-700">{r.serial}</span><Badge className="bg-rose-50 text-rose-700 border border-rose-200 text-[10px]">×{r.count}</Badge></div>
          ))}
          {!a.repeat_failure_serials.length && <Empty />}
        </Panel>
        <Panel title="Warranty recovery by vendor">
          <div className="flex justify-between text-[11px] uppercase text-slate-400 mb-1"><span>Vendor</span><span>Recovered / Pending</span></div>
          {a.recovery.by_vendor.map((v) => (
            <div key={v.vendor} className="flex justify-between text-sm py-1"><span className="text-slate-700">{v.vendor}</span>
              <span><span className="text-emerald-700">{inr(v.recovered)}</span> / <span className="text-rose-700">{inr(v.pending)}</span></span></div>
          ))}
          {!a.recovery.by_vendor.length && <Empty />}
          <div className="mt-2 pt-2 border-t border-slate-100 text-sm flex justify-between">
            <span className="text-slate-500">Total pending recovery</span><span className="font-semibold text-rose-700">{inr(a.recovery.pending_total)}</span>
          </div>
        </Panel>
      </div>
    </div>
  );
}
function SlaView({ s }) {
  if (!s) return <div className="text-sm text-slate-400">No SLA data.</div>;
  const rows = [
    ["Request → Approval", s.request_to_approval_h],
    ["Receipt → Inspection", s.receipt_to_inspection_h],
    ["Inspection → Resolution", s.inspection_to_resolution_h],
    ["Overall TAT", s.overall_tat_h],
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="rma-sla">
      <div className="grid grid-cols-4 gap-2 px-4 py-2 text-[11px] font-semibold uppercase text-slate-400 border-b border-slate-100">
        <div>Stage</div><div className="text-right">Avg (h)</div><div className="text-right">Median (h)</div><div className="text-right">P90 (h)</div>
      </div>
      {rows.map(([label, m]) => (
        <div key={label} className="grid grid-cols-4 gap-2 px-4 py-3 text-sm border-b border-slate-50">
          <div className="text-slate-700 flex items-center gap-1.5"><Gauge className="h-3.5 w-3.5 text-slate-400" />{label}</div>
          <div className="text-right tabular-nums">{m?.avg ?? "—"}</div>
          <div className="text-right tabular-nums">{m?.median ?? "—"}</div>
          <div className="text-right tabular-nums">{m?.p90 ?? "—"} <span className="text-[10px] text-slate-400">n={m?.n ?? 0}</span></div>
        </div>
      ))}
    </div>
  );
}
const Panel = ({ title, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-sm font-semibold text-slate-800 mb-2">{title}</div>{children}</div>
);
const Empty = () => <div className="text-sm text-slate-400 py-2">No data yet.</div>;
const Bar = ({ label, v, max }) => (
  <div className="py-1">
    <div className="flex justify-between text-sm"><span className="text-slate-700 truncate pr-2">{label}</span><span className="tabular-nums text-slate-500">{v}</span></div>
    <div className="h-1.5 rounded-full bg-slate-100 mt-0.5"><div className="h-1.5 rounded-full bg-slate-800" style={{ width: (max ? (v / max) * 100 : 0) + "%" }} /></div>
  </div>
);

// -------------------------------------------------------------- Case drawer with stage actions
function CaseDrawer({ c, meta, onClose, reload }) {
  const [busy, setBusy] = useState(false);
  const act = async (path, body) => {
    setBusy(true);
    try { await api.post(`/rma/cases/${c.id}/${path}`, body || {}); toast.success("Updated"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  const rec = c.recovery || {};
  const insp = c.inspection || {};
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="rma-drawer">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-3 flex items-center justify-between z-10">
          <div>
            <div className="text-sm font-semibold text-slate-900">{c.rma_no}</div>
            <div className="text-[11px] text-slate-400">{c.product || "—"} · {c.serial || "no serial"}</div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={"text-[10px] border " + (STATUS_TINT[c.status] || "")}>{c.status?.replace(/_/g, " ")}</Badge>
            <button onClick={onClose} data-testid="rma-drawer-close" className="p-1 rounded hover:bg-slate-100"><X className="h-4 w-4 text-slate-500" /></button>
          </div>
        </div>
        <div className="p-5 space-y-5 text-sm">
          {/* eligibility */}
          <Section title="Eligibility" icon={ShieldQuestion}>
            <Badge className={"text-[10px] border " + (ELIG_TINT[c.eligibility?.result] || "")}>{c.eligibility?.result?.replace(/_/g, " ") || "—"}</Badge>
            <ul className="mt-2 space-y-0.5 text-[13px] text-slate-600 list-disc pl-5">
              {(c.eligibility?.reasons || []).map((r, i) => <li key={i}>{r}</li>)}
            </ul>
            {(c.fraud_flags || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {c.fraud_flags.map((f) => <Badge key={f} className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px]">{f.replace(/_/g, " ")}</Badge>)}
              </div>
            )}
            {meta?.can_write && <Button size="sm" variant="outline" className="mt-2" disabled={busy} onClick={() => act("eligibility")} data-testid="rma-recheck">Re-check</Button>}
          </Section>

          {meta?.can_write && (
            <Section title="Actions">
              <div className="flex flex-wrap gap-2">
                {["REQUESTED", "ELIGIBILITY_REVIEW"].includes(c.status) && (
                  <>
                    <Button size="sm" disabled={busy} onClick={() => act("approve", { replacement_cost: 0 })} data-testid="rma-approve" className="bg-emerald-600 hover:bg-emerald-700 text-white">Approve</Button>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => act("reject", { note: "Rejected" })} data-testid="rma-reject" className="text-rose-600 border-rose-200">Reject</Button>
                  </>
                )}
                {c.status === "APPROVED" && <Button size="sm" disabled={busy} onClick={() => act("collection", { method: "REVERSE_PICKUP" })} data-testid="rma-collection">Book collection</Button>}
                {c.status === "COLLECTION_PENDING" && <Button size="sm" disabled={busy} onClick={() => act("receive", { serial_scanned: c.serial, package_condition: "OK" })} data-testid="rma-receive"><PackageCheck className="h-3.5 w-3.5 mr-1" />Mark received</Button>}
                {c.status === "RECEIVED" && <Button size="sm" disabled={busy} onClick={() => act("inspection", { fault_confirmed: true })} data-testid="rma-inspect">Start inspection</Button>}
                {c.status === "INSPECTION" && (
                  <>
                    <Button size="sm" disabled={busy} onClick={() => act("decision", { outcome: "REPAIR" })} data-testid="rma-decide-repair"><Wrench className="h-3.5 w-3.5 mr-1" />Repair</Button>
                    <Button size="sm" disabled={busy} onClick={() => act("decision", { outcome: "REPLACE" })} data-testid="rma-decide-replace"><RefreshCw className="h-3.5 w-3.5 mr-1" />Replace</Button>
                    <Button size="sm" disabled={busy} onClick={() => act("decision", { outcome: "OEM_VENDOR_RMA" })} data-testid="rma-decide-oem"><Factory className="h-3.5 w-3.5 mr-1" />OEM RMA</Button>
                  </>
                )}
                {["DISPATCH", "DELIVERED", "REPAIR", "REPLACEMENT", "OEM_RMA", "RECOVERY_PENDING"].includes(c.status) && (
                  <>
                    {["REPAIR", "REPLACEMENT"].includes(c.status) && <Button size="sm" disabled={busy} onClick={() => act("dispatch", {})} data-testid="rma-dispatch">Dispatch</Button>}
                    {c.status === "DISPATCH" && <Button size="sm" disabled={busy} onClick={() => act("deliver")} data-testid="rma-deliver">Mark delivered</Button>}
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => act("close")} data-testid="rma-close">Close</Button>
                  </>
                )}
              </div>
            </Section>
          )}

          {/* inspection summary */}
          {insp.inspected_at && (
            <Section title="Inspection" icon={Wrench}>
              <Kv k="Fault confirmed" v={insp.fault_confirmed ? "Yes" : "No"} />
              <Kv k="Fault code" v={insp.fault_code} /><Kv k="Grade" v={insp.condition_grade} />
              <Kv k="Warranty eligible" v={insp.warranty_eligible ? "Yes" : "No"} />
            </Section>
          )}

          {/* OEM / recovery — internal only */}
          {(c.supplier_rma || rec.status !== "NOT_APPLICABLE") && (
            <Section title="OEM / Warranty Recovery" icon={Factory}>
              {c.supplier_rma && (<><Kv k="Vendor" v={c.supplier_rma.vendor} /><Kv k="Claim no." v={c.supplier_rma.claim_number} /><Kv k="Claim amount" v={inr(c.supplier_rma.claim_amount)} /></>)}
              <Kv k="Recovery status" v={rec.status?.replace(/_/g, " ")} />
              <Kv k="Recoverable" v={inr(rec.recoverable_amount)} />
              <Kv k="Recovered" v={inr(rec.recovered_amount)} />
              <Kv k="Pending" v={inr(rec.pending_recovery)} />
              {meta?.can_write && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => act("supplier-rma", { vendor: c.brand || "OEM", submission_date: new Date().toISOString().slice(0, 10) })} data-testid="rma-supplier">Log OEM claim</Button>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => act("recovery", { recovered_amount: rec.recoverable_amount || 0, status: "RECOVERED" })} data-testid="rma-recovered">Mark recovered</Button>
                </div>
              )}
            </Section>
          )}

          {/* costs — internal only */}
          {c.costs && Object.keys(c.costs).length > 0 && (
            <Section title="Cost (internal only)" icon={IndianRupee}>
              {Object.entries(c.costs).map(([k, v]) => <Kv key={k} k={k.replace(/_/g, " ")} v={inr(v)} />)}
            </Section>
          )}

          {/* timeline */}
          <Section title="Timeline">
            <ol className="space-y-2">
              {(c.timeline || []).slice().reverse().map((t, i) => (
                <li key={i} className="flex gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5" />
                  <div><div className="text-[13px] text-slate-700">{t.event.replace(/_/g, " ")}{t.note ? ` — ${t.note}` : ""}</div>
                    <div className="text-[11px] text-slate-400">{(t.at || "").slice(0, 16).replace("T", " ")} · {t.by}</div></div>
                </li>
              ))}
            </ol>
          </Section>
        </div>
      </div>
    </div>
  );
}
const Section = ({ title, icon: Icon, children }) => (
  <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">{Icon && <Icon className="h-3.5 w-3.5" />}{title}</div>{children}</div>
);
const Kv = ({ k, v }) => (
  <div className="flex justify-between py-0.5 text-[13px]"><span className="text-slate-500">{k}</span><span className="text-slate-800">{v || "—"}</span></div>
);

// -------------------------------------------------------------- create
function CreateDialog({ meta, onClose, onCreated }) {
  const [f, setF] = useState({ customer_name: "", serial: "", product: "", reason_code: "HARDWARE_FAILURE", issue: "", origin: "INTERNAL" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/rma/cases", f); toast.success(`RMA ${data.rma_no} created · ${data.eligibility?.result?.replace(/_/g, " ")}`); onCreated(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create RMA"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="rma-create-modal">
        <DialogHeader><DialogTitle>New RMA request</DialogTitle></DialogHeader>
        <div className="space-y-2.5">
          <Input placeholder="Customer" value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} data-testid="rma-f-customer" />
          <Input placeholder="Serial number" value={f.serial} onChange={(e) => setF({ ...f, serial: e.target.value })} data-testid="rma-f-serial" />
          <Input placeholder="Product (auto-filled from serial if known)" value={f.product} onChange={(e) => setF({ ...f, product: e.target.value })} data-testid="rma-f-product" />
          <select className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" value={f.reason_code}
            onChange={(e) => setF({ ...f, reason_code: e.target.value })} data-testid="rma-f-reason">
            {(meta?.reason_codes || []).map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
          </select>
          <textarea rows={3} placeholder="Describe the issue" value={f.issue} onChange={(e) => setF({ ...f, issue: e.target.value })}
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm resize-none" data-testid="rma-f-issue" />
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="rma-create-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create & check eligibility"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
